/**
 * Alan ikonları — çerçeve katmanı (Katman 1).
 *
 * Bunlar UYARAN DEĞİL. Uyaran alanının dışında, başlık şeridinde
 * duruyorlar; `shapes.ts`'ten ayrı bir dosyada olmalarının sebebi de bu.
 * İkisi karışırsa bir gün biri uyaran havuzuna düşer ve çocuk bir
 * ızgarada "dikkat ikonunu" aramaya başlar — görevi şablon değil, grafik
 * tercihi değiştirmiş olur (`dokuman/gorsellik.md` §0).
 *
 * Neden dört alan? Çocuk "bu hafta dört ayrı şey var" bilgisini ikondan
 * alıyor; uygulayıcı da ödev paketine bakınca dengeyi tek bakışta
 * görüyor. Motorun yedi `Domain`'i bunun yerine kullanılamaz: `maze`'in
 * alanları `working_memory_visual` + `selective_attention` ama ailesi
 * uzamsal. Yedi alan ÖLÇÜMÜN ekseni, dördü çocuğa gösterilen gruplama.
 *
 * Çizim kuralı: tek renk kontur, dolgusuz. Fotokopide ayakta kalsın —
 * kâğıt ödev ikinci üçüncü elden çoğaltılıyor. Renk hiçbir şey taşımıyor
 * (§0.1): ayrımın tamamı biçimde.
 */

export type AlanKodu = 'dikkat' | 'bellek' | 'akil' | 'uzamsal'

export const ALAN_KODLARI: AlanKodu[] = ['dikkat', 'bellek', 'akil', 'uzamsal']

export const ALAN_ADI: Record<AlanKodu, string> = {
  dikkat: 'Dikkat',
  bellek: 'Bellek',
  akil: 'Akıl yürütme',
  uzamsal: 'Uzamsal',
}

/**
 * Her ikon bir ya da birkaç yoldan oluşuyor, hepsi 24×24 kutuda.
 *
 * Çok yollu: daireyi ve artı işaretini tek `d` içinde birleştirmek
 * okunaksız olurdu ve `stroke-linecap` davranışı yollar arasında
 * değişiyor. Liste hâlinde durmaları ayrıca bir yolu değiştirirken
 * ötekini bozma riskini kaldırıyor.
 */
export const ALAN_IKONU: Record<AlanKodu, string[]> = {
  /**
   * Hedef: çember + dört çentik + merkez.
   *
   * Dikkat egzersizlerinin tamamı bir hedef arama ya da bir hedefte
   * kalma işi; nişangâh bunu anlatan en kısa biçim.
   */
  dikkat: [
    'M12 5A7 7 0 1 0 12 19A7 7 0 1 0 12 5Z',
    'M12 2V5M12 19V22M2 12H5M19 12H22',
    'M12 10.4A1.6 1.6 0 1 0 12 13.6A1.6 1.6 0 1 0 12 10.4Z',
  ],
  /**
   * Üst üste iki kart.
   *
   * Bellek görevleri bir şeyi tutup üstüne bir şey daha koyma işi;
   * iki kart bunun en az çizgiyle anlatımı. Beyin silueti denenmedi:
   * 24 pikselde lekeye dönüyor ve fotokopide kayboluyor.
   */
  bellek: ['M8 4H20V16H8Z', 'M4 8H16V20H4Z'],
  /**
   * 2×2 matris, dördüncü hücre köşe çentikleriyle.
   *
   * Dördüncü hücre tam çizilseydi ikon düz bir ızgara olurdu — akıl
   * yürütmenin işi ise eksik olanı bulmak. Çentikler "burası boş"
   * diyor, soru işareti gibi bir sembole gerek kalmadan.
   */
  akil: [
    'M4 4H11V11H4Z',
    'M13 4H20V11H13Z',
    'M4 13H11V20H4Z',
    'M13 15V13H15M18 13H20V15M20 18V20H18M15 20H13V18',
  ],
  /**
   * İzometrik küp.
   *
   * Dönme oku da denenebilirdi ama ok yön taşıyor ve yön, uzamsal
   * egzersizlerin bir kısmında (ayna görüntüsü) yanlış ipucu. Küp
   * yönsüz: yalnızca "üç boyut" diyor.
   */
  uzamsal: ['M5 8L12 4L19 8V16L12 20L5 16Z', 'M5 8L12 12L19 8M12 12V20'],
}

/**
 * Kâğıt kanalı için ikon SVG'si.
 *
 * `shapes.ts`'teki `sekilSvg` ile aynı sözleşme: dize olarak dönüyor,
 * kapalı bir kümeden geliyor, dışarıdan hiçbir şey içine girmiyor.
 * Renk `currentColor` — saran öğe belirliyor.
 */
export function alanIkonuSvg(alan: AlanKodu, boyutPx?: number): string {
  const olcu = boyutPx ? ` width="${boyutPx}" height="${boyutPx}"` : ''
  const yollar = ALAN_IKONU[alan]
    .map((d) => `<path d="${d}"/>`)
    .join('')
  return (
    `<svg viewBox="0 0 24 24"${olcu} xmlns="http://www.w3.org/2000/svg" ` +
    `role="img" aria-label="${ALAN_ADI[alan]}" focusable="false" ` +
    `fill="none" stroke="currentColor" stroke-width="1.8" ` +
    `stroke-linecap="round" stroke-linejoin="round">${yollar}</svg>`
  )
}
